import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getCurrentUser, fetchAuthSession, signIn, confirmSignIn, signOut } from 'aws-amplify/auth';
import '@/lib/amplify-config';

const AuthContext = createContext();

/**
 * Auth state, backed by CareConnect's shared Cognito user pool (see
 * infra/lib/auth-stack.js) — this app has no user pool, and no signup, of its own.
 *
 * Sign-in is native email/password via Amplify `signIn`, mirroring how CareConnect
 * itself authenticates (careconnect/src/lib/awsCognitoAuth.js). This is deliberate,
 * not incidental: CareConnect's pool has self-service signup enabled at the pool
 * level for CareConnect's own onboarding, and the Cognito Hosted UI would surface
 * that "Sign up" link on this site too. A native form never renders one — only
 * someone who already has a CareConnect account can ever get in here.
 *
 * Role: `role` is 'product_owner' when the CareConnect ID token's `cognito:groups`
 * includes that group, otherwise 'user' for any other authenticated CareConnect
 * account (care_giver, nurse, staff_admin, ...). The pre-token-generation Lambda on
 * CareConnect's pool also injects a pile of tenant-scoping pseudo-groups (raw IDs,
 * `w_admin:<id>`, the user's own email, ...) into the same claim — a plain
 * `.includes('product_owner')` check is safe against that noise, it just never
 * collides with a real group name.
 *
 * The site is PUBLIC. Marketing pages must render for signed-out visitors, so a
 * failed session check is a normal state, not an error. Only the training portal
 * gates (see TrainingAuthGate.jsx).
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const checkUserAuth = useCallback(async () => {
    setIsLoadingAuth(true);
    setAuthError(null);
    try {
      const current = await getCurrentUser();
      const session = await fetchAuthSession();
      const claims = session?.tokens?.idToken?.payload || {};

      const groups = normaliseGroups(claims['cognito:groups']);

      setUser({
        id: claims.sub || current.userId,
        email: claims.email || null,
        full_name: claims.fullname || claims.name || current.username || null,
        role: groups.includes('product_owner') ? 'product_owner' : 'user',
        groups,
      });
      setIsAuthenticated(true);
    } catch {
      // Signed out. Expected on every public page — not an error condition.
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoadingAuth(false);
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    checkUserAuth();
  }, [checkUserAuth]);

  /**
   * Email/password sign-in. Returns `{ newPasswordRequired: true }` for the rare
   * FORCE_CHANGE_PASSWORD case (an AdminCreateUser'd CareConnect account that has
   * never completed its first CareConnect login) — the caller should collect a new
   * password and call `completeNewPasswordChallenge`. Throws on any other failure;
   * `preventUserExistenceErrors` on the app client means a wrong password and an
   * unknown email produce the same generic error, so this never confirms whether an
   * email has a CareConnect account.
   */
  const login = useCallback(async ({ email, password }) => {
    setAuthError(null);
    try {
      // An Amplify session may already exist (e.g. re-login after a stale token);
      // clear it first so signIn doesn't throw "already signed in".
      try {
        await signOut();
      } catch {
        /* no active session */
      }

      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' },
      });

      if (isSignedIn) {
        await checkUserAuth();
        return {};
      }

      if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return { newPasswordRequired: true };
      }

      throw new Error('Sign-in could not be completed. Please try again.');
    } catch (err) {
      const message =
        err?.name === 'NotAuthorizedException' || err?.name === 'UserNotFoundException'
          ? 'Incorrect email or password.'
          : err?.message || 'Sign-in failed. Please try again.';
      setAuthError({ type: 'sign_in_failed', message });
      throw new Error(message);
    }
  }, [checkUserAuth]);

  /** Completes the FORCE_CHANGE_PASSWORD challenge `login()` may return. */
  const completeNewPasswordChallenge = useCallback(async (newPassword) => {
    setAuthError(null);
    try {
      const { isSignedIn, nextStep } = await confirmSignIn({ challengeResponse: newPassword });
      if (!isSignedIn) {
        throw new Error(
          nextStep?.signInStep && nextStep.signInStep !== 'DONE'
            ? `Additional sign-in step required: ${nextStep.signInStep}`
            : 'Setting the new password could not be completed.'
        );
      }
      await checkUserAuth();
    } catch (err) {
      const message = err?.message || 'Could not set the new password. Please try again.';
      setAuthError({ type: 'sign_in_failed', message });
      throw new Error(message);
    }
  }, [checkUserAuth]);

  const logout = useCallback(async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Sign-out failed', err);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoadingAuth,
        // Retained so App.jsx keeps its existing loading branch. Cognito resolves
        // config locally, so there is no separate settings fetch to wait on.
        isLoadingPublicSettings: false,
        authError,
        authChecked,
        isProductOwner: user?.role === 'product_owner',
        login,
        completeNewPasswordChallenge,
        logout,
        checkUserAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/** `cognito:groups` arrives as an array or a bracketed string depending on the token. */
function normaliseGroups(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    return raw.replace(/^\[|\]$/g, '').split(/[,\s]+/).filter(Boolean);
  }
  return [];
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
